/**
 * Who may open Admin Setup (tenant configuration).
 *
 * Helix platform users, or an admin of a customer-side company. Supplier users
 * have no tenant-level config to manage. "Helix" is decided by the user's
 * platform / company type, never by Django's is_staff flag, which test data
 * has set on tenant accounts before (CR-5367).
 *
 * Lives here rather than inline because the rule is read in two places — the
 * sidebar's Admin Setup link and the Admin Setup page itself, which a user can
 * still reach by typing the URL.
 */
import type { User } from './api/types/common';

type AdminSetupUser = Pick<User, 'is_company_admin' | 'platform' | 'company_type'> | null | undefined;

/**
 * @param user - the authenticated user, or null/undefined when not loaded yet
 * @param inSupplierView - CR-8217: the user has switched their portal to
 *   Supplier View. The API treats them as a supplier and refuses Admin Setup
 *   saves, so the page stays hidden until they switch back.
 * @returns true when this user may open Admin Setup
 */
export function canOpenAdminSetup(user: AdminSetupUser, inSupplierView = false): boolean {
  if (!user) return false;
  const isHelix = user.platform === 'HELIX' || user.company_type === 'helix';
  if (isHelix) return true;
  if (inSupplierView) return false;
  const isSupplier = user.company_type === 'supplier' || user.platform === 'SUPPLIER';
  return !isSupplier && Boolean(user.is_company_admin);
}

/**
 * CR-8217: Helix, or a user of a customer company — the side that owns the
 * customer configuration. A logistics admin can open Admin Setup for its own
 * branding, organisation and notification noise controls, but the API refuses
 * it the customer-only sections and the install-wide notification switches.
 *
 * @param user - the authenticated user, or null/undefined when not loaded yet
 * @returns true for Helix and customer-company users
 */
export function isCustomerSideUser(user: AdminSetupUser): boolean {
  if (!user) return false;
  return user.platform === 'HELIX' || user.company_type === 'helix' || user.company_type === 'customer';
}

/**
 * CR-8238: a Helix platform user — the only users whose saves reach the
 * platform-wide visibility settings (dashboard, purchasing and quality tabs,
 * supplier card fields, Data Import tiles). Mirrors backend IsHelixUser.
 *
 * @param user - the authenticated user, or null/undefined when not loaded yet
 * @returns true for a Helix platform user
 */
export function isHelixPlatformUser(user: AdminSetupUser): boolean {
  return Boolean(user && (user.platform === 'HELIX' || user.company_type === 'helix'));
}
